import React, { ReactNode, useEffect, useState } from 'react';

import { overlays, inputs, actions, feedback, feedbackV2 } from '@ecosystem/newron-design-system';

const { Modal } = overlays;
const { Checkbox, CheckboxState } = inputs;
const { Button } = actions;

export enum ExportFormat {
  Xlsx = 'xlsx',
  Pptx = 'pptx',
  Png = 'png',
}

export type ExportModalProps = {
  isShown: boolean;
  title: string;
  message: string;
  exportFormats: ExportFormat[];
  disabledFormats?: ExportFormat[];
  errorMessage?: string;
  download: (selectedExportFormats: ExportFormat[]) => Promise<void>;
  onClose: () => void;
  onError: (error: Error) => void;
};

enum ExportState {
  None,
  InProgress,
  Completed,
}

const exportFormatToLabelMap = {
  [ExportFormat.Xlsx]: 'Excel (.xlsx)',
  [ExportFormat.Pptx]: 'Powerpoint (.pptx)',
  [ExportFormat.Png]: 'Image (.png)',
};

export function ExportModal({
  isShown,
  title,
  message,
  exportFormats,
  disabledFormats,
  errorMessage,
  download,
  onClose,
  onError,
}: Readonly<ExportModalProps>): JSX.Element {
  const [exportState, setExportState] = useState(ExportState.None);
  const [selectedExportFormats, setSelectedExportFormats] = useState<ExportFormat[]>([]);

  useEffect(() => {
    if (isShown && exportFormats.length === 1 && !disabledFormats?.includes(exportFormats[0])) {
      setSelectedExportFormats([...exportFormats]);
    }
  }, [exportFormats, isShown, disabledFormats]);

  const onDownload = async () => {
    if (exportState !== ExportState.None) {
      return;
    }

    setExportState(ExportState.InProgress);

    try {
      await download(selectedExportFormats);

      setExportState(ExportState.Completed);
      setTimeout(() => {
        setExportState(ExportState.None);
        onCloseModal();
      }, 1000);
    } catch (error) {
      setExportState(ExportState.None);
      onError(error);
    }
  };

  const toggleExportFormat = (exportFormat: ExportFormat) => {
    if (selectedExportFormats.includes(exportFormat)) {
      setSelectedExportFormats(selectedExportFormats.filter(format => format !== exportFormat));
    } else {
      setSelectedExportFormats([...selectedExportFormats, exportFormat]);
    }
  };

  const onCloseModal = () => {
    setSelectedExportFormats([]);
    onClose();
  };

  const getButtonIcon = (exportState: ExportState): ReactNode => {
    switch (exportState) {
      case ExportState.Completed:
        return (
          <Button.Icon>
            <Button.Icon.icons.Check />
          </Button.Icon>
        );

      case ExportState.InProgress:
        return <Button.Loader />;

      case ExportState.None:
      default:
        return (
          <Button.Icon>
            <Button.Icon.icons.Export />
          </Button.Icon>
        );
    }
  };

  return (
    <Modal onClose={onCloseModal} isOpen={isShown} renderInPortal>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        {errorMessage && (
          <div data-test-id="export-alert-error">
            <feedbackV2.Alert type="error" title={errorMessage} size={feedback.AlertSize.SMALL} />
          </div>
        )}
        <p>{message}</p>
        <div data-test-id="export-options-wrapper" className="flex-row gap-8 mt-4">
          {exportFormats.map(exportFormat => (
            <Checkbox
              key={exportFormat}
              disabled={exportState !== ExportState.None || disabledFormats?.includes(exportFormat)}
              state={selectedExportFormats.includes(exportFormat) ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
              onChange={() => toggleExportFormat(exportFormat)}
              testId={`export-${exportFormat}-option`}
              label={exportFormatToLabelMap[exportFormat]}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          mode="secondary"
          onClick={onCloseModal}
          disabled={exportState !== ExportState.None}
          testId="export-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={onDownload}
          testId="export-download"
          disabled={exportState === ExportState.InProgress || !selectedExportFormats.length}
        >
          {getButtonIcon(exportState)}
          Download
          {!!selectedExportFormats.length && <Button.Badge value={selectedExportFormats.length.toString()} />}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
